// src/components/Pages/About.jsx
import React, { useState, useEffect, useRef } from 'react';
import './About.css';
import { FaLightbulb, FaUsers, FaHeart, FaMapMarkerAlt, FaPhone, FaEnvelope, FaClock, FaExternalLinkAlt } from 'react-icons/fa';
import Team from '../Pages/Team';

// Free OpenStreetMap Component (No API Key Required!)
const FreeMap = () => {
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapError, setMapError] = useState(false);
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);

  // Company coordinates (Replace with your actual coordinates)
  const companyLocation = {
    lat: 0.25605468645300933,  // Replace with your actual latitude (Kampala example)
    lng: 32.619717, // Replace with your actual longitude
    address: "Kajja Galleries, Kampala, Uganda",
    zoom: 15
  };

  useEffect(() => {
    let isMounted = true;

    const initMap = () => {
      if (!mapContainerRef.current || mapInstanceRef.current) return;
      if (!window.L) return;

      try {
        // Create map instance
        const map = window.L.map(mapContainerRef.current).setView([companyLocation.lat, companyLocation.lng], companyLocation.zoom);
        
        // Add OpenStreetMap tiles
        window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
          maxZoom: 19
        }).addTo(map);
        
        // Add custom marker
        const customIcon = window.L.divIcon({
          html: '<div style="font-size: 32px;">📍</div>',
          iconSize: [32, 32],
          className: 'custom-marker'
        });
        
        window.L.marker([companyLocation.lat, companyLocation.lng], { icon: customIcon })
          .addTo(map)
          .bindPopup(`
            <strong>Kajja Galleries</strong><br/>
            ${companyLocation.address}<br/>
            <a href="https://www.google.com/maps/dir/?api=1&destination=${companyLocation.lat},${companyLocation.lng}" target="_blank" style="color: maroon;">Get Directions →</a>
          `)
          .openPopup();
        
        // Add scale bar
        window.L.control.scale().addTo(map);
        
        // Store map instance for cleanup
        mapInstanceRef.current = map;
        
        // Fix map sizing after render
        setTimeout(() => {
          if (mapInstanceRef.current) {
            mapInstanceRef.current.invalidateSize();
          }
        }, 100);
        
        if (isMounted) {
          setMapLoaded(true);
        }
      } catch (error) {
        console.error('Error initializing map:', error);
        if (isMounted) {
          setMapError(true);
        }
      }
    };

    const loadLeaflet = () => {
      // Check if Leaflet is already loaded
      if (window.L) {
        initMap();
        return;
      }

      // Load Leaflet CSS
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      link.integrity = 'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=';
      link.crossOrigin = '';
      document.head.appendChild(link);

      // Load Leaflet JS
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      script.integrity = 'sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo=';
      script.crossOrigin = '';
      script.onload = () => {
        initMap();
      };
      script.onerror = () => {
        if (isMounted) {
          setMapError(true);
        }
      };
      document.head.appendChild(script);
    };

    loadLeaflet();

    // Cleanup function to remove map when component unmounts
    return () => {
      isMounted = false;
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  // Get directions link
  const getDirectionsLink = () => {
    const url = `https://www.google.com/maps/dir/?api=1&destination=${companyLocation.lat},${companyLocation.lng}`;
    window.open(url, '_blank');
  };

  // Open in OpenStreetMap
  const openInOSM = () => {
    const url = `https://www.openstreetmap.org/?mlat=${companyLocation.lat}&mlon=${companyLocation.lng}&zoom=15`;
    window.open(url, '_blank');
  };

  if (mapError) {
    return (
      <div style={{ 
        padding: '40px', 
        textAlign: 'center', 
        background: '#f9fafb', 
        borderRadius: '16px',
        border: '1px solid #e5e7eb'
      }}>
        <FaMapMarkerAlt size={48} color="maroon" style={{ marginBottom: '16px' }} />
        <h3 style={{ marginBottom: '8px' }}>Unable to load map</h3>
        <p style={{ color: '#666', marginBottom: '16px' }}>Please click the button below to view our location.</p>
        <button 
          onClick={getDirectionsLink}
          style={{
            background: 'linear-gradient(135deg, maroon, #5a0016)',
            color: 'white',
            border: 'none',
            padding: '10px 24px',
            borderRadius: '8px',
            cursor: 'pointer'
          }}
        >
          Get Directions
        </button>
      </div>
    );
  }

  return (
    <div className="free-map-section">
      <div style={{ marginBottom: '16px', display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FaMapMarkerAlt color="maroon" />
            <strong>{companyLocation.address}</strong>
          </div>
          <p style={{ fontSize: '13px', color: '#666', marginTop: '4px' }}>
            Click on the pin for directions
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button 
            onClick={getDirectionsLink}
            style={{
              background: 'linear-gradient(135deg, maroon, #5a0016)',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '13px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            🗺️ Get Directions <FaExternalLinkAlt size={10} />
          </button>
          <button 
            onClick={openInOSM}
            style={{
              background: '#f3f4f6',
              color: '#374151',
              border: '1px solid #e5e7eb',
              padding: '8px 16px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '13px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            View Larger Map <FaExternalLinkAlt size={10} />
          </button>
        </div>
      </div>
      
      <div 
        ref={mapContainerRef}
        id="free-map" 
        style={{ 
          height: '400px', 
          width: '100%', 
          borderRadius: '16px', 
          overflow: 'hidden',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          background: '#f0f0f0'
        }} 
      />
      
      {!mapLoaded && !mapError && (
        <div style={{ 
          height: '400px', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          flexDirection: 'column',
          gap: '12px',
          background: '#f9fafb',
          borderRadius: '16px'
        }}>
          <div className="loading-spinner"></div>
          <p>Loading map...</p>
        </div>
      )}
    </div>
  );
};

// Contact Info Component
const ContactInfo = () => {
  return (
    <div className="contact-info-section">
      <h2 style={{ color: 'maroon', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
        📞 Contact Us
      </h2>
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
        gap: '20px',
        marginBottom: '30px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '16px', background: '#f9fafb', borderRadius: '12px' }}>
          <FaPhone size={24} color="maroon" />
          <div>
            <div style={{ fontWeight: '600' }}>Phone</div>
            <div style={{ color: '#666' }}>+256 709 728 710</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '16px', background: '#f9fafb', borderRadius: '12px' }}>
          <FaEnvelope size={24} color="maroon" />
          <div>
            <div style={{ fontWeight: '600' }}>Email</div>
            <div style={{ color: '#666' }}>kajjagalleries@gmail.com</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '16px', background: '#f9fafb', borderRadius: '12px' }}>
          <FaClock size={24} color="maroon" />
          <div>
            <div style={{ fontWeight: '600' }}>Business Hours</div>
            <div style={{ color: '#666' }}>Mon - Sat: 9:00 AM - 6:00 PM</div>
          </div>
        </div>
      </div>
    </div>
  );
};

const About = () => {
  return (
    <div className="about-container">
      <header className="about-header">
        <h1 style={{color:'maroon'}}>About Kajja Galleries</h1>
        <p>Your creative home for inspired living and artistic expression.</p>
      </header>

      <section className="about-cards">
        <div className="card">
          <FaLightbulb className="icon" />
          <h2>Our Vision</h2>
          <p>To create better living spaces for human kind globally</p>
        </div>
        <div className="card">
          <FaUsers className="icon" />
          <h2>Our Mission</h2>
          <p>To deliver high quality, functional furniture solutions for businesses, focusing on innovation, sustainability and long term partnerships.</p>
        </div>
        <div className="card">
          <FaHeart className="icon" />
          <h2>Our Values</h2>
          <p>Creativity, Trust, and Effeciency in everything we do.</p>
        </div>
      </section>

      <section className="about-profile">
        <img style={{height:"250px"}} src="./images/artbanner.jpg" alt="Gallery" />
        <div>
          <h2 style={{color:'maroon'}}>Who We Are</h2>
          <p>
          We are Kajja Galleries — creators of efficient, elegant furniture built for business, hospitality, and modern spaces.
          </p>
          <p><a href='./images/CompanyProfile.pdf' target="_blank" rel="noopener noreferrer"> <button className='view-button3'>View Company Profile</button></a></p>
        </div>
      </section>

      {/* Free Map Section - No API Key Required! */}
      <section className="about-maps-section" style={{ marginTop: '40px', padding: '20px', background: 'white', borderRadius: '16px' }}>
        <h2 style={{ color: 'maroon', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <FaMapMarkerAlt /> Find Us
        </h2>
        <FreeMap />
      </section>

      {/* Contact Information */}
      <section className="about-contact-section" style={{ marginTop: '20px', padding: '20px', background: 'white', borderRadius: '16px' }}>
        <ContactInfo />
      </section>

      <Team />
    </div>
  );
};

export default About;